"use client";

import { MapPin, Navigation, Plane, Car } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useState } from "react";
export default function DestinationsWeServe() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const [hoveredDestination, setHoveredDestination] = useState<number | null>(null);
  const destinations = [{
    id: 1,
    name: "Connecticut",
    top: "28%",
    left: "68%",
    delay: "100ms",
    description: "Serving all of Connecticut with premium transportation",
    details: "Complete coverage across Hartford, New Haven, Stamford, and all CT cities",
    regionId: "region-ct"
  }, {
    id: 2,
    name: "New York City",
    top: "48%",
    left: "65%",
    delay: "200ms",
    description: "Direct service to all NYC boroughs and airports",
    details: "Manhattan, Brooklyn, Queens, Bronx, and Staten Island - JFK, LGA, EWR access",
    regionId: "region-nyc"
  }, {
    id: 3,
    name: "New Jersey",
    top: "62%",
    left: "58%",
    delay: "300ms",
    description: "Coverage across major New Jersey cities and airports",
    details: "Newark, Jersey City, Hoboken - Full EWR and TEB airport service",
    regionId: "region-nj"
  }];
  return <section ref={sectionRef} className="relative py-20 px-4 sm:px-6 lg:px-8 bg-background overflow-hidden">
      {}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto">
        {}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 mb-4">
            <Navigation className={`w-6 h-6 text-accent transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 rotate-0" : "opacity-0 rotate-45"}`} />
            <span className={`text-sm font-semibold text-accent uppercase tracking-wider transition-all duration-[var(--duration-medium)] ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4"}`}>
              Our Service Area
            </span>
          </div>
          <h2 className={`text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6 transition-all duration-[var(--duration-medium)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            Destinations We Serve
          </h2>
          <p className={`max-w-3xl mx-auto text-lg sm:text-xl text-muted leading-relaxed transition-all duration-[var(--duration-medium)] delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            Premium transportation services connecting Connecticut, New York City, 
            Long Island, and New Jersey with unmatched reliability and comfort
          </p>
        </div>

        {}
        

        {}
        <div className={`mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 transition-all duration-[var(--duration-medium)] delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {destinations.map((dest, i) => <div key={dest.id} data-index={i} className="group relative" onMouseEnter={() => setHoveredDestination(dest.id)} onMouseLeave={() => setHoveredDestination(null)}>
              <Card className={`h-full bg-secondary border-2 shadow-soft hover:shadow-strong transition-all duration-[var(--duration-medium)] hover:-translate-y-2 p-8 ${hoveredDestination === dest.id ? "border-accent/60" : "border-border/40"}`}>
                {}
                <div className="relative mb-6">
                  <div className={`absolute inset-0 rounded-full blur-xl transition-all duration-300 ${hoveredDestination === dest.id ? "bg-accent/30" : "bg-accent/10"}`} />
                  <div className={`relative w-16 h-16 mx-auto rounded-full flex items-center justify-center transition-transform duration-300 ${hoveredDestination === dest.id ? "bg-accent/30 scale-110" : "bg-accent/20 scale-100"}`}>
                    <MapPin className="w-8 h-8 text-accent" strokeWidth={2.5} />
                  </div>
                </div>

                {}
                <div className="text-center">
                  <h3 className={`font-display font-bold text-xl mb-3 transition-colors duration-300 ${hoveredDestination === dest.id ? "text-accent" : "text-foreground-secondary"}`}>
                    {dest.name}
                  </h3>
                  <p className="text-muted text-sm leading-relaxed mb-2">
                    {dest.description}
                  </p>
                  
                </div>

                {}
                <div className={`absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-accent/10 to-transparent rounded-bl-3xl transition-opacity duration-300 ${hoveredDestination === dest.id ? "opacity-100" : "opacity-0"}`} />
              </Card>
            </div>)}
        </div>

        {}
        <div className={`mt-16 text-center transition-all duration-[var(--duration-medium)] delay-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="text-muted text-lg">
            Connecting major airports including <span className="font-semibold text-foreground">JFK, LGA, EWR, BDL, HPN, and TEB</span>
          </p>
        </div>
      </div>

      {}
      <style>{`
        @keyframes dash {
          to {
            stroke-dashoffset: -100;
          }
        }
      `}</style>
    </section>;
}